import { createFileRoute, redirect } from '@tanstack/react-router';
import { cn } from '@workspace/ui/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';

import { ChatInitial } from '~/components/chat/initial';
import { ChatInput } from '~/components/chat/input';
import { ChatMessages } from '~/components/chat/messages';
import { ENABLE_CHAT } from '~/config/environment';
import { useChat } from '~/hooks/use-chat/use-chat';

export const Route = createFileRoute('/_authenticated/chat/$chatId/')({
  validateSearch: (search: Record<string, string>): { q?: string } => {
    return {
      q: search.q,
    };
  },
  beforeLoad() {
    if (!ENABLE_CHAT) {
      throw redirect({ to: '/' });
    }
  },
  component: ChatPage,
});

function ChatPage() {
  const { chatId } = Route.useParams();
  const { q } = Route.useSearch();

  const {
    messages,
    input,
    isLoading,
    handleInputChange,
    handleSubmit,
    attachedFiles,
    handleFileSelect,
    removeFile,
    status,
    handleToolCall,
    thinkings,
    currentMessageHasToolCall,
  } = useChat({
    initialChatId: chatId,
    initialMessage: q,
  });

  const [isDragging, setIsDragging] = useState(false);
  const isInitial = messages.value.length === 0;

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (e.dataTransfer.files) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect],
  );

  return (
    <div
      className="relative flex flex-col min-w-0 h-dvh bg-background"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="application"
      aria-label="Chat interface with file drop support"
    >
      <motion.div
        key="chat"
        className={cn(
          'flex-1 flex flex-col items-center transition-all duration-500 ease-in-out',
          isInitial
            ? 'justify-center'
            : 'justify-start overflow-y-scroll pt-14 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar scrollbar-thumb-primary-foreground scrollbar-track-background',
        )}
      >
        <div className="w-full max-w-3xl flex flex-col items-center gap-6 px-4 md:px-2">
          <AnimatePresence mode="wait">
            <motion.div
              key="messages"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="w-full flex-1"
            >
              {isInitial && <ChatInitial />}
              <ChatMessages
                status={status}
                messages={messages}
                handleAction={handleToolCall}
                thinkings={thinkings}
              />
            </motion.div>
          </AnimatePresence>

          {isInitial && (
            <div className="w-full">
              <ChatInput
                isLoading={isLoading}
                isDragging={isDragging}
                input={input}
                attachedFiles={attachedFiles}
                handleFileSelect={handleFileSelect}
                handleSubmit={handleSubmit}
                removeFile={removeFile}
                handleInputChange={handleInputChange}
                currentMessageHasToolCall={currentMessageHasToolCall}
                status={status}
              />
            </div>
          )}
        </div>
      </motion.div>

      {!isInitial && (
        <div
          className={cn(
            'border-t py-4 bg-background transition-all duration-300',
            currentMessageHasToolCall && 'blur-xs',
          )}
        >
          <div className="max-w-3xl mx-auto">
            <ChatInput
              isLoading={isLoading}
              isDragging={isDragging}
              input={input}
              attachedFiles={attachedFiles}
              handleFileSelect={handleFileSelect}
              handleSubmit={handleSubmit}
              removeFile={removeFile}
              handleInputChange={handleInputChange}
              currentMessageHasToolCall={currentMessageHasToolCall}
              status={status}
            />
          </div>
        </div>
      )}
    </div>
  );
}
